import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { MappedStakeholder } from "src/core/constants/folder";
import { useFolder } from "src/core/contexts/FolderContext";
import { useStakeholderDocument } from "src/core/hooks/folder/useStakeholderDocument";
import { withRequiredOverride } from "src/core/overrides";
import { DocumentField } from "src/core/utils/documents";

const DocumentsUploadFieldsetOverride = withRequiredOverride(
  "components.folder.DocumentsUploadFieldset"
);

type StakeholderDocumentModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  stakeholder: MappedStakeholder;
  documentToUpload: DocumentField;
};

export const StakeholderDocumentModal = ({
  open,
  setOpen,
  stakeholder,
  documentToUpload,
}: StakeholderDocumentModalProps) => {
  const { t } = useTranslation();
  const { isReadOnly, isPPH } = useFolder();
  const { form, handleSubmit, isLoading } = useStakeholderDocument(
    stakeholder,
    documentToUpload,
    setOpen
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        className="w-[400px]"
      >
        <DialogHeader className="mb-30">
          <DialogTitle>
            {stakeholder.isFolderCustomer && !isPPH
              ? stakeholder.socialReason
              : isPPH &&
                (stakeholder.firstName === t("form.createClient.emptyPPHName") ||
                  stakeholder.lastName === t("form.createClient.emptyPPHName"))
              ? t("form.createClient.emptyPPHName")
              : `${stakeholder.firstName} ${stakeholder.lastName}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("stakeholderDocument.modal.description")}
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DocumentsUploadFieldsetOverride
              requiredDocuments={[documentToUpload]}
              disabled={isReadOnly}
              className="folder-modal-document"
            />

            <DialogFooter className="flex flex-row justify-center gap-x-40 border-t border-border pt-10 sticky bottom-0 bg-gray-xlight h-fit">
              <Button
                variant="link"
                onClick={(e) => {
                  e.preventDefault();
                  form.reset();
                  setOpen?.(false);
                }}
              >
                {t(isReadOnly ? "global.form.close" : "global.form.cancel")}
              </Button>
              {!isReadOnly && (
                <Button type="submit" loading={isLoading}>
                  {t("global.form.submit")}
                </Button>
              )}
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
